import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { fetchResourceList, createResource, updateResource, deleteResource } from "../../utils/api";
import { DEFAULT_QUERY_CONFIG } from "./config/queryConfig";
import { useState } from "react";
import { Paginated } from "@common/shared/paginated.model";
import { useDisclosure } from "@chakra-ui/react";
import { useSelection } from "./hooks/useSelection";
import { SortConfig } from "./@types/sort-config";
import { ResourceContextType } from "./@types/resource-context-type";
import { ResourceListDefaults } from "./@types/resource-list-defaults";
import { SortOrder } from "./@types/sort-order";
import { Resource } from "./@types/resource.type";

interface UseResourceListProps {
  resource: Resource;
  defaults?: ResourceListDefaults;
  params?: Record<string, string>;
}

export function useResourceList<T>({
  resource,
  defaults,
  params,
}: UseResourceListProps): ResourceContextType<T> {
  const queryClient = useQueryClient();

  const initialSortConfig = defaults?.sortConfig ?? {
    field: "created_at",
    direction: "desc",
  };

  const initialPageSize = defaults?.pageSize ?? DEFAULT_QUERY_CONFIG.PAGE_SIZE;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortConfig, setSortConfig] = useState<SortConfig>(initialSortConfig);
  const [filterText, setFilterText] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedOne, setSelectedOne] = useState<T | null>(null);

  function handleOpenModal(object?: T) {
    setSelectedOne(object || null);
    onOpen();
  }

  function handleCloseModal() {
    setSelectedOne(null);
    onClose();
  }

  function getQueryKey(page: number, size: number) {
    return [resource, page, size, sortConfig.field, sortConfig.direction, filterText, params];
  }

  function invalidateCache() {
    queryClient.invalidateQueries({ queryKey: [resource] });
  }

  function handleFilterText(text: string) {
    setFilterText(text);
    setCurrentPage(1);
  }

  const queryOptions: UseQueryOptions<Paginated<T>> = {
    queryKey: getQueryKey(currentPage, pageSize),
    queryFn: async () => {
      const result = await fetchResourceList<T>(resource.path, {
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        sortField: sortConfig.field,
        sortOrder: sortConfig.direction,
        filterText,
        ...params,
      });
      return result;
    },
    staleTime: DEFAULT_QUERY_CONFIG.STALE_TIME,
  };

  const { data, isLoading, error, isFetching, refetch } = useQuery(queryOptions);

  const handleSort = (field: string, order?: SortOrder) => {
    if (field === sortConfig.field && !order) {
      setSortConfig({
        field,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
      return;
    }

    setSortConfig({
      field,
      direction: order || "desc",
    });
  };

  const createMutation = useMutation({
    mutationFn: (data: Partial<T>) => createResource<T>(resource.path, data),
    onSuccess: () => {
      setSortConfig(initialSortConfig);
      setCurrentPage(1);
      invalidateCache();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<T> }) => {
      await updateResource<T>(resource.path, id, data);
      return { id, data };
    },
    onSuccess: result => {
      queryClient.setQueryData(getQueryKey(currentPage, pageSize), (oldItems: Paginated<T>) => {
        if (!oldItems) return oldItems;

        return {
          ...oldItems,
          data: oldItems.data.map((item: any) =>
            item.id === result.id ? { ...item, ...result.data } : item
          ),
        };
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteResource(resource.path, id),
    onSuccess: () => {
      invalidateCache();
    },
  });

  const selection = useSelection<T>({
    data: data?.data || [],
  });

  return {
    resource,
    params,
    data: data || {
      data: [],
      page: currentPage,
      pageSize: pageSize,
      totalPages: 1,
      totalCount: 0,
    },
    isLoading,
    isFetching,
    error,
    pagination: {
      page: currentPage,
      setPage: setCurrentPage,
      pageSize,
      setPageSize,
    },
    selection,
    sort: {
      config: sortConfig,
      setSort: handleSort,
    },
    filter: {
      text: filterText,
      setText: handleFilterText,
    },
    modal: {
      isOpen,
      handleOpenModal,
      handleCloseModal,
      selectedOne,
    },
    actions: {
      refetch: () => refetch().then(() => {}),
      createItem: createMutation.mutateAsync,
      updateItem: (id: string, data: Partial<T>) =>
        updateMutation.mutateAsync({ id, data }).then(() => {}),
      deleteItem: deleteMutation.mutateAsync,
    },
  };
}
